// src/components/AuthenticatedLayout.jsx
import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar/Sidebar';
import MobileTopBar from './MobileTopBar/MobileTopBar';
import MobileBottomBar from './MobileBottomBar/MobileBottomBar';
import PortraitLock from './Responsive/PortraitLock';
import { useIsMobile } from '../hooks/useIsMobile';
import useDrawerBackNavigation from '../hooks/useDrawerBackNavigation';

const AuthenticatedLayout = ({ children }) => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  // The top bar is the projects LIST's own chrome, not app-wide furniture:
  // it is the one mobile screen whose scrollport reserves a strip for it
  // (padding-top on .projects-content) so it can scroll away without leaving
  // a gap. Every other screen — project view, RSA-Analysis — renders straight
  // to the top edge with no bar and no reserved space.
  // Exact match, same test MobileBottomBar uses to pick its variant: the
  // /projects/:id and /projects/:id/view sub-routes are the project view.
  const showTopBar = pathname === '/projects';
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Which page the drawer is showing. Lives here rather than in Sidebar
  // because the back-gesture handler below has to read it.
  const [drawerView, setDrawerView] = useState('nav');

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Reset so reopening never lands on Settings unexpectedly.
    setDrawerView('nav');
  }, []);

  const backToNav = useCallback(() => setDrawerView('nav'), []);

  // Mobile: the OS back gesture steps settings -> nav -> closed. The two
  // handlers it hands back drive the SAME channel for the on-screen back
  // arrow and the backdrop, so the history stack never desynchronises from
  // the drawer's state — see the hook for the two bugs that requires.
  const { goBack, requestClose } = useDrawerBackNavigation({
    active: isMobile && drawerOpen,
    view: drawerView,
    onBack: backToNav,
    onClose: closeDrawer,
  });

  // Desktop: byte-identical to the original shell.
  if (!isMobile) {
    return (
      <div className='authenticated-layout'>
        <Sidebar />
        <div className='main-content'>{children}</div>
      </div>
    );
  }

  // Mobile (≤768px): slim top bar + off-canvas drawer instead of the rail.
  return (
    <div
      className={`authenticated-layout authenticated-layout--mobile${
        showTopBar ? ' authenticated-layout--has-topbar' : ''
      }`}
    >
      <PortraitLock />
      {/* The marker class above is what the projects list keys its top-bar
          clearance off, so the reserved strip can never outlive the bar. */}
      {showTopBar && <MobileTopBar />}
      {/* The drawer trigger lives in the bottom bar now; search is relayed
          as a window event so the projects screen can own its panel without
          the shell knowing about it. */}
      <MobileBottomBar
        onMenuClick={() => setDrawerOpen(true)}
        onSearchClick={() =>
          window.dispatchEvent(new CustomEvent('rsa:mobile-search-toggle'))
        }
      />
      <Sidebar
        variant='drawer'
        open={drawerOpen}
        view={drawerView}
        onViewChange={setDrawerView}
        onBack={goBack}
        onClose={requestClose}
      />
      <div className='main-content main-content--mobile'>{children}</div>
    </div>
  );
};

export default AuthenticatedLayout;
